import React, { useMemo, useRef, useState } from 'react'
import { ISalePrices, IMemberPrice } from '@/model/types/goods'
import useBemClass from '@/utils/hooks/useBEMClass'
import { intervalSalePrices } from '@/utils/goods'
import Iconfont from '@/components/iconfont/iconfont'
import { Column } from '@/components/layout/layout'
import InputPopover from '@/views/main/modules/goods-manage/components/input-popover/input-popover'
import { INPUT_PRICE_LIMIT_REGEXP } from '@/utils'
import classname from 'classnames'
import { useDynamicList, useUpdateEffect } from 'ahooks'
import LadderPricePopover from './ladder-price-popover'
import { goodsModel } from '../../edit-goods/modules/model'
import { showLayer } from '@/utils/layer'
import ChooseGoodsUnitDialog from '../../edit-goods/modules/goods-unit/goods-unit'

interface BatchPriceEditProps {
    salePrices: BatchSalePrices[]
    onChange: (price: BatchSalePrices[]) => void
}

export interface BatchSalePrices extends ISalePrices {
    new_sale_price: string | number
    new_threshold: string | number
}

interface BatchMemberPriceEditProps {
    memberPrice: BatchMemeberPrice
    onChange: (price: BatchMemeberPrice) => void
    maxValue: number
}

interface BatchUnitEditProps {
    unit: BatchUnit
    onChange: (unit: BatchUnit) => void
}

export interface BatchMemeberPrice extends IMemberPrice {
    new_sale_price: string | number
}

export interface BatchUnit {
    name: string
    id: number
    old_id: number
}

export const BatchUnitEdit: React.FC<BatchUnitEditProps> = props => {
    const [unit, setUnit] = useState<BatchUnit>(props.unit)

    return (
        <div
            data-cursor-pointer
            data-width-100p
            data-row
            data-align-items-center
            data-justify-content-space-between
            onClick={() => {
                showLayer(
                    <ChooseGoodsUnitDialog
                        goodsModel={goodsModel}
                        choosedId={unit.id}
                        onChange={params => {
                            const tmp = {
                                ...params,
                                old_id: unit.id,
                            }
                            setUnit(tmp)
                            if (params.id !== unit.id) {
                                props.onChange(tmp)
                            }
                        }}
                    ></ChooseGoodsUnitDialog>,
                )
            }}
        >
            元/{unit.name}
            <Iconfont name="icon6youjiantou-20" />
        </div>
    )
}

export const BatchMemberPriceEdit: React.FC<BatchMemberPriceEditProps> = props => {
    const cls = useBemClass('batch-price')
    const [memberPrice, setMemberPrice] = useState<BatchMemeberPrice>(props.memberPrice)

    const hasMemberPrice = useMemo(() => props.memberPrice.new_sale_price, [props.memberPrice])

    const isPriceChange = useMemo(() => {
        return memberPrice.new_sale_price !== memberPrice.sale_price
    }, [memberPrice.new_sale_price, memberPrice.sale_price])

    const content = useMemo(
        () => (
            <div className={cls('edit-ipt')}>
                <div
                    onClick={e => {
                        e.stopPropagation()
                        setMemberPrice(val => ({
                            ...val,
                            new_sale_price: val.sale_price,
                        }))
                    }}
                >
                    {isPriceChange && <Iconfont name="iconchehui" size={16} />}
                </div>

                <Column data-align-items-flex-end>
                    {isPriceChange && <div>¥{memberPrice.new_sale_price}</div>}
                    {(!hasMemberPrice ? !isPriceChange : true) && (
                        <div
                            className={classname({
                                [cls('edit-ipt', 'del')]: isPriceChange,
                            })}
                        >
                            {memberPrice.sale_price ? `¥${memberPrice.sale_price}` : '-'}
                        </div>
                    )}
                </Column>
            </div>
        ),
        [cls, hasMemberPrice, isPriceChange, memberPrice.new_sale_price, memberPrice.sale_price],
    )

    useUpdateEffect(() => {
        isPriceChange && props.onChange(memberPrice)
    }, [memberPrice])

    return (
        <InputPopover
            des="¥"
            onConfirm={val => {
                setMemberPrice(v => ({
                    ...v,
                    new_sale_price: parseFloat(val),
                }))
            }}
            onValidate={e => {
                if (parseFloat(e) === 0) {
                    return '价格需大于0'
                }
                if (parseFloat(e) >= props.maxValue) {
                    return '会员价需小于售价'
                }

                return ''
            }}
            value={memberPrice.new_sale_price ? memberPrice.new_sale_price + '' : ''}
            reg={INPUT_PRICE_LIMIT_REGEXP}
        >
            {content}
        </InputPopover>
    )
}

const BatchPriceEdit: React.FC<BatchPriceEditProps> = props => {
    const cls = useBemClass('batch-price')
    const initVal = useRef<BatchSalePrices[]>(
        props.salePrices.map(s => ({
            ...s,
            new_sale_price: s.sale_price,
            new_threshold: s.threshold,
        })),
    )
    const { list: price, replace, resetList } = useDynamicList<BatchSalePrices>(props.salePrices)

    const isPriceChange = useMemo(() => {
        if (price.length !== props.salePrices.length) return true

        return price.some(p => p.new_sale_price !== p.sale_price)
    }, [price, props.salePrices.length])

    const isLimitChage = useMemo(() => {
        if (price.length !== props.salePrices.length) return true

        return price.some(p => p.new_threshold !== p.threshold)
    }, [price, props.salePrices.length])

    const content = useMemo(
        () => (
            <div className={cls('edit-ipt')}>
                <div
                    onClick={e => {
                        e.stopPropagation()
                        resetList(initVal.current)
                    }}
                >
                    {isPriceChange && <Iconfont name="iconchehui" size={16} />}
                </div>
                <Column data-align-items-flex-end>
                    {isPriceChange && (
                        <div>
                            ¥
                            {price.length > 1
                                ? `${intervalSalePrices(price, 'new_sale_price').min}～${
                                      intervalSalePrices(price, 'new_sale_price').max
                                  }`
                                : price[0].new_sale_price}
                        </div>
                    )}
                    <div
                        className={classname({
                            [cls('edit-ipt', 'del')]: isPriceChange,
                        })}
                    >
                        ¥
                        {props.salePrices.length > 1
                            ? `${intervalSalePrices(props.salePrices).min}～${intervalSalePrices(props.salePrices).max}`
                            : props.salePrices[0].sale_price}
                    </div>
                </Column>
            </div>
        ),
        [cls, isPriceChange, price, props.salePrices, resetList],
    )

    useUpdateEffect(() => {
        if (isLimitChage || isPriceChange) props.onChange(price)
    }, [price])

    return props.salePrices.length > 1 ? (
        <div
            onClick={() => {
                showLayer(
                    <LadderPricePopover
                        salePrice={price}
                        onConfirm={price => {
                            resetList(price)
                        }}
                    />,
                )
            }}
        >
            {content}
        </div>
    ) : (
        <InputPopover
            des="¥"
            onConfirm={val => {
                replace(0, {
                    ...price[0],
                    new_sale_price: parseFloat(val),
                })
            }}
            onValidate={e => {
                if (parseFloat(e) === 0) {
                    return '价格需大于0'
                }

                return ''
            }}
            placeholder="请输入改后价"
            value={price[0].new_sale_price + ''}
            reg={INPUT_PRICE_LIMIT_REGEXP}
        >
            {content}
        </InputPopover>
    )
}

export default BatchPriceEdit
